// ./src/hooks/useMemberships.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  Membership,
  MembershipService,
  MembershipFilter,
  MembershipInput,
  MembershipBulkInput,
  MembershipPurgeScope,
  Pagination,
} from "@/utils/api";
import { SortItem, listPage } from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseMembershipsOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changing after mount has no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — call list(newFilters) to change.
   */
  initialFilters?: Partial<MembershipFilter>;

  /**
   * Sort applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — pass sort to list() to change.
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseMembershipsReturn {
  // Data state
  membership: Membership | null;
  memberships: Membership[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  /** True while a bulk status/expiry change is in flight. */
  isBulkUpdating: boolean;
  /** True while a bulk generate is in flight. */
  isGenerating: boolean;
  /** True while a purge is in flight. */
  isPurging: boolean;
  /** IDs currently being removed — supports many concurrent removals. */
  removingIds: Set<string>;
  /** True when `id` is being removed (or, with no id, when any removal is in flight). */
  isRemoving: (id?: string) => boolean;
  selectedMembership: Membership | null;

  // Filter and sort
  filters: Partial<MembershipFilter>;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<MembershipFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: MembershipInput) => Promise<OpResult>;
  update: (id: string, params: MembershipInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  /** Mass-change status and/or expiry on many memberships at once. */
  bulkUpdate: (ids: string[], params: MembershipBulkInput) => Promise<OpResult>;
  /** Bulk-issue `count` memberships for an internal type. */
  generate: (membershipTypeId: string, count: number) => Promise<OpResult>;
  /** Remove expired memberships, optionally scoped to internal/external. */
  purge: (scope?: MembershipPurgeScope) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedMembership: (membership: Membership | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getById: (id: string | undefined) => Membership | undefined;

  // Derived data
  activeMemberships: Membership[];
  totalMembershipsCount: number;
  activeMembershipsCount: number;
  hasMemberships: boolean;

  // Error handling
  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "membershipNumber", order: "ASC" }];

// Only fields the API's MembershipFilter accepts may go here. Keep it empty so
// the initial autoLoad lists everything scoped to the account.
const DEFAULT_FILTERS: Partial<MembershipFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useMemberships = (
  options: UseMembershipsOptions = {}
): UseMembershipsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters/sort for loadMore/refresh
  const lastQueriedFilterRef = useRef<Partial<MembershipFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [membership, setMembership] = useState<Membership | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] = useState<Partial<MembershipFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [selectedMembership, setSelectedMembership] = useState<Membership | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && memberships.length === 0,
    [isLoaded, memberships.length]
  );

  // ==========================================
  // Removal tracking (supports many at once)
  // ==========================================

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  // ==========================================
  // Helpers
  // ==========================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setMembership(null);
    setMemberships([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setIsGenerating(false);
    setIsPurging(false);
    setRemovingIds(new Set());
    setSelectedMembership(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await MembershipService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as Membership[])[0];
        setMembership(data);
        setMemberships((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setMembership(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setMembership(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List memberships.
   * Race-condition safe via listCallIdRef.
   */
  const list = useCallback(
    async (
      filterOverride: Partial<MembershipFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      // Store queried filters/sort in refs
      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await listPage(
          MembershipService.list,
          filterOverride as MembershipFilter,
          paginationParams,
          currentSortBy
        );

        // Guard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as Membership[])
            : [];

          if (isAppending) {
            setMemberships((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setMemberships(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMemberships([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMemberships([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await MembershipService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data)
          ? (result.data as Membership[])
          : [];
        setMemberships((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(async (params: MembershipInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await MembershipService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as Membership[])[0];
        setMembership(newItem);
        setMemberships((prev) => upsertBy(prev, newItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, params: MembershipInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Membership ID is required for updating"
        );
        setLastError(result);

        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await MembershipService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Membership[])[0];
          setMembership(updatedItem);
          setMemberships((prev) => upsertBy(prev, updatedItem));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Membership ID is required for removal"
        );
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await MembershipService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setMembership(null);
          setMemberships((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one membership ID is required for removal"
        );
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await MembershipService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setMembership(null);
          setMemberships((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  // Mass-change status and/or expiry across many memberships. The server applies
  // the same patch to every id and returns the updated rows, which we merge into
  // the local list so the change shows without a full refresh.
  const bulkUpdate = useCallback(
    async (ids: string[], params: MembershipBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one membership must be selected"
        );
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await MembershipService.bulkUpdate(ids, params);

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data)
            ? (result.data as Membership[])
            : [];
          setMemberships((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  // ==========================================
  // Bulk actions (generate / purge)
  // ==========================================

  // These do not mutate the local list — the caller refreshes on success so the
  // paginated, sorted view stays consistent.
  const generate = useCallback(
    async (membershipTypeId: string, count: number): Promise<OpResult> => {
      setIsGenerating(true);
      setLastError(null);

      try {
        const result = await MembershipService.generate(membershipTypeId, count);

        if (result.code !== OP_RESULT_CODES.OK) {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const purge = useCallback(
    async (scope: MembershipPurgeScope = "all"): Promise<OpResult> => {
      setIsPurging(true);
      setLastError(null);

      try {
        const result = await MembershipService.purge(scope);

        if (result.code !== OP_RESULT_CODES.OK) {
          setLastError(result);
        }

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsPurging(false);
      }
    },
    []
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  // ==========================================
  // Lookup Helpers
  // ==========================================

  const getById = useCallback(
    (id: string | undefined): Membership | undefined => {
      if (!id) {
        return undefined;
      }

      return memberships.find((item) => item.id === id);
    },
    [memberships]
  );

  // ==========================================
  // Derived Data
  // ==========================================

  const activeMemberships = useMemo(
    () => memberships.filter((item) => item.status === STATUSES.ACTIVE),
    [memberships]
  );

  const totalMembershipsCount = useMemo(() => memberships.length, [memberships]);

  const activeMembershipsCount = useMemo(
    () => activeMemberships.length,
    [activeMemberships]
  );

  const hasMemberships = useMemo(
    () => activeMemberships.length > 0,
    [activeMemberships]
  );

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      membership,
      memberships,
      loading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      isBulkUpdating,
      isGenerating,
      isPurging,
      removingIds,
      isRemoving,
      selectedMembership,

      // Filter and sort
      filters,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      generate,
      purge,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedMembership,

      // Helper operations
      reset,

      // Lookup helpers
      getById,

      // Derived data
      activeMemberships,
      totalMembershipsCount,
      activeMembershipsCount,
      hasMemberships,

      // Error handling
      lastError,
      clearError,
    }),
    [
      membership,
      memberships,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      isGenerating,
      isPurging,
      removingIds,
      isRemoving,
      selectedMembership,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      generate,
      purge,
      loadMore,
      refresh,
      reset,
      getById,
      activeMemberships,
      totalMembershipsCount,
      activeMembershipsCount,
      hasMemberships,
      lastError,
      clearError,
    ]
  );
};
